/**
 * StrategyPerformance — table of outreach strategies sorted by reply rate.
 */

import { useQuery } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { useApi } from '@/hooks/use-api'

type StrategyData = {
  readonly name: string
  readonly sent: number
  readonly replies: number
  readonly reply_rate: number
  readonly positive_rate: number
}

type StrategiesResponse = {
  readonly strategies: readonly StrategyData[]
}

const STRATEGY_DISPLAY: Record<string, string> = {
  pain_agitate: 'Pain Agitate',
  mutual_value: 'Mutual Value',
  social_proof: 'Social Proof',
  curiosity_gap: 'Curiosity Gap',
  direct_expert: 'Direct Expert',
  challenge: 'Challenge',
  trojan_horse: 'Trojan Horse',
  mutual_connection: 'Mutual Connection',
}

function RateBadge({ rate }: { readonly rate: number }) {
  const color = rate >= 15 ? 'bg-emerald-500/12 text-emerald-300' : rate >= 5 ? 'bg-amber-500/12 text-amber-300' : 'bg-red-500/12 text-red-300'
  return <Badge className={`text-[10px] border-0 ${color}`}>{rate}%</Badge>
}

export function StrategyPerformance() {
  const apiFetch = useApi()

  const { data, isLoading } = useQuery({
    queryKey: ['dashboard', 'strategies'],
    queryFn: async (): Promise<StrategiesResponse> => {
      const res = await apiFetch<StrategiesResponse>('/api/leads/analytics/strategies')
      if (!res.success || !res.data) return { strategies: [] }
      return res.data
    },
    staleTime: 60_000,
  })

  const strategies = data?.strategies ?? []

  if (isLoading) {
    return (
      <Card>
        <CardHeader><CardTitle className="text-sm font-semibold">Strategy Performance</CardTitle></CardHeader>
        <CardContent><Skeleton className="h-48 w-full" /></CardContent>
      </Card>
    )
  }

  if (strategies.length === 0) {
    return (
      <Card>
        <CardHeader><CardTitle className="text-sm font-semibold">Strategy Performance</CardTitle></CardHeader>
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          No outreach data yet. Strategy performance will appear once messages are sent.
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold">Strategy Performance</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="divide-y">
          {strategies.map((s) => (
            <div key={s.name} className="flex items-center justify-between px-4 py-2.5">
              <div className="min-w-0">
                <p className="text-sm font-medium">{STRATEGY_DISPLAY[s.name] ?? s.name}</p>
                <p className="text-xs text-muted-foreground">{s.sent} sent &middot; {s.replies} replies</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <RateBadge rate={s.reply_rate} />
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}
